import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChatTransport, SSEFrame, StreamBody } from './transport'
import {
  BUSY_CODE,
  BUSY_RETRY_DELAYS_MS,
  DEFAULT_STALL_RETRIES,
  DEFAULT_STALL_TIMEOUT_MS,
  INITIAL_RETRY_STATE,
  STALL_RETRY_DELAY_MS,
  TurnBusyError,
  abortableDelay,
  createStallWatchdog,
  planRetry,
  retryBodyFor,
} from './turnRetry'
import type { AttemptOutcome, RetryPolicy, RetryState } from './turnRetry'
import {
  RECOVERED_FAILURE_DETAIL,
  RECOVERED_NO_THREAD_DETAIL,
  RECOVER_DEADLINE_MS,
  RECOVER_MAX_FETCH_FAILURES,
  RECOVER_POLL_MS,
  planRecovery,
} from './turnRecovery'
import type { PendingTurnInfo, PendingTurnKind, RecoveryAction } from './turnRecovery'
import type {
  ChatMessage,
  ClarifyQuestion,
  DownloadInfo,
  LoadedMessage,
  LoadedThread,
  ThinkingEntry,
  TurnFailure,
} from './types'

// Small coercion helpers — SSE payloads are `unknown`.
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.map(str) : [])
const isAbort = (err: unknown): boolean => (err as { name?: string } | null)?.name === 'AbortError'

interface PersistedChat {
  threadId: string | null
  messages: ChatMessage[]
  pendingQuestion: ClarifyQuestion | null
  // Identity (tenant+user) that produced this transcript. Stamped on save and
  // verified on load so a blob left in shared localStorage by one login is never
  // adopted by another on a single-origin app — the thread_id especially, which
  // the backend rejects with 403 ("does not belong to client …") when it carries
  // a different tenant's thread. Absent for unscoped callers (the widget).
  owner?: string | null
  // A turn was IN FLIGHT when this was written (the page went away mid-answer).
  // The server keeps running such turns to completion, so the next mount polls
  // the thread replay and adopts the answer instead of dropping the turn.
  // Cleared by the settled write once the turn ends normally.
  pendingTurn?: { kind: PendingTurnKind } | null
}

function loadPersisted(key: string, owner?: string): PersistedChat | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const p = JSON.parse(raw) as PersistedChat
    if (!Array.isArray(p?.messages)) return null
    // Owner-scoped callers (the dashboard passes the signed-in tenant) may only
    // rehydrate their OWN persisted chat. A missing or different stamp means the
    // blob is a prior login's — refuse it rather than resume its thread_id.
    if (owner !== undefined && (p.owner ?? null) !== owner) return null
    return {
      threadId: p.threadId ?? null,
      messages: p.messages,
      pendingQuestion: p.pendingQuestion ?? null,
      owner: p.owner ?? null,
      pendingTurn: p.pendingTurn ?? null,
    }
  } catch {
    return null
  }
}

function savePersisted(key: string, p: PersistedChat): void {
  try {
    localStorage.setItem(key, JSON.stringify(p))
  } catch {
    // storage full/unavailable — non-fatal; the chat just won't survive refresh.
  }
}

function clearPersisted(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

// Highest assistant-bubble id in a (restored) transcript. turnSeq must start
// ABOVE this on mount: restored bubbles keep the ids they were persisted with,
// but the counter otherwise restarts at 0 — so the first new turn would reuse
// id 1, collide with the first restored reply, and patch-by-id (findIndex hits
// the OLD bubble first) would paint the answer onto it, leaving the new bubble
// blank. That's the "reply doesn't render after a refresh / navigating back"
// bug. loadThread already keeps ids ahead; this covers the initial restore.
function maxAssistantId(messages: ChatMessage[] | undefined): number {
  let mx = 0
  for (const m of messages ?? []) {
    if (m.role === 'assistant' && m.id > mx) mx = m.id
  }
  return mx
}

// Trailing user bubble of a transcript — what an orphaned turn was asking.
// Recovery compares it against the thread replay to tell "our question got its
// answer" from "the run died before our question was even checkpointed".
function lastUserText(messages: readonly { role: string; content?: string }[] | undefined): string | null {
  for (let i = (messages?.length ?? 0) - 1; i >= 0; i--) {
    const m = messages![i]
    if (m.role === 'user') return m.content ?? null
  }
  return null
}

// Token pacing. The network (and the SSE proxy) deliver tokens in bursts; if we
// render each one the instant it lands the text appears in lumpy spurts. Instead
// we buffer incoming tokens and drain them on a steady requestAnimationFrame
// cadence: at least PACE_MIN chars/frame for a smooth typewriter trickle, but
// `ceil(backlog / PACE_DIV)` when behind so a big burst catches up within a few
// frames instead of lagging seconds behind the model. This also collapses many
// per-token React updates into at most one per frame.
const PACE_MIN = 3
const PACE_DIV = 6

/** Passed to `onStall` each time the progress watchdog trips. */
export interface StallInfo {
  /** The send that stalled (1 = the original). */
  attempt: number
  /** Whether the hook is about to re-send automatically. */
  willRetry: boolean
  timeoutMs: number
  threadId: string | null
}

/** Passed to `onFailed` when a turn is given up. */
export interface TurnFailedInfo extends TurnFailure {
  /** Sends made before giving up. */
  attempts: number
  threadId: string | null
}

export interface UseChatOptions {
  /** HOW to open the stream — auth/headers/refresh live in here (per host). */
  stream: ChatTransport
  /** Persist transcript + thread under this localStorage key so a refresh
   *  restores the conversation. Omit for an ephemeral chat. */
  persistKey?: string
  /** Fetch a past conversation by id (history). Required for `loadThread(id)`
   *  to fetch; if a caller passes messages to loadThread directly, unused. */
  loadThreadMessages?: (id: string) => Promise<LoadedThread>
  /** Token pacing (rAF typewriter). Default true; pass false to append instantly. */
  paced?: boolean
  /** Identity that owns the persisted chat (e.g. `clientId:username`). When set,
   *  the transcript+thread are stamped with it and only rehydrated for the same
   *  owner, and changing it tears the current chat down — so a single-origin app
   *  that switches logins in place can never send the prior tenant's thread_id.
   *  Omit for an unscoped chat (the widget runs inside one authenticated host). */
  owner?: string
  /** Progress watchdog: abort the turn when no SSE frame (token, tool call,
   *  node, …) has arrived for this long. Heartbeat comments never reach the
   *  reducer, so this trips on a wedged model — the transport's byte watchdog
   *  covers a dead socket. Default 90s; 0 disables. */
  stallTimeoutMs?: number
  /** Automatic re-sends of a stalled turn before it is marked failed. Default 1. */
  stallRetries?: number
  /** Called each time the watchdog trips (before any automatic re-send). */
  onStall?: (info: StallInfo) => void
  /** Called when a turn is given up — stalled out of retries, or the stream
   *  errored. The bubble carries the same failure (`message.failed`). */
  onFailed?: (info: TurnFailedInfo) => void
  /** Ask the backend to stop the run on a thread (POST
   *  /chat/threads/{id}/cancel). Turns survive a bare disconnect server-side,
   *  so Stop and the stall retry's abort call this to actually kill one —
   *  without it they only stop watching. Fire-and-forget; errors ignored. */
  cancelTurn?: (threadId: string) => void | Promise<unknown>
}

export interface UseChat {
  messages: ChatMessage[]
  isLoading: boolean
  pendingQuestion: ClarifyQuestion | null
  threadId: string | null
  /** Send a user message. `opts.incidentId` marks the turn as a structured
   *  incident investigation — the backend routes it deterministically into the
   *  alert-investigation flow instead of classifying the text. */
  send: (text: string, opts?: { incidentId?: string }) => void
  sendResume: (value: string, label?: string) => void
  /** Interrupt the in-flight turn: abort the stream, keep whatever has already
   *  streamed into the bubble, and hand control back to the user. No-op when
   *  nothing is in flight. */
  stop: () => void
  /** Re-send the last turn after it failed (`messages` ends with an assistant
   *  bubble carrying `failed`). The bubble is reused — no second user message,
   *  no second bubble. No-op while a turn is in flight or nothing has failed. */
  retry: () => void
  startNewChat: () => void
  /** Replace the current transcript with a past conversation and resume it.
   *  Pass `messages` to load them directly (host already fetched them), or omit
   *  to fetch via `loadThreadMessages`. Rejects (leaving the current chat
   *  untouched) if a fetch fails. */
  loadThread: (id: string, messages?: LoadedMessage[]) => Promise<void>
}

type AssistantMessage = Extract<ChatMessage, { role: 'assistant' }>

export function useChat({
  stream,
  persistKey,
  loadThreadMessages,
  paced = true,
  owner,
  stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
  stallRetries = DEFAULT_STALL_RETRIES,
  onStall,
  onFailed,
  cancelTurn,
}: UseChatOptions): UseChat {
  // Rehydrate any persisted chat so a refresh restores the transcript — but only
  // the current owner's (see loadPersisted). Lazy `useState(initializer)` runs
  // the read exactly once.
  const [restored] = useState(() => (persistKey ? loadPersisted(persistKey, owner) : null))
  // A restored pendingTurn means a turn was in flight when the page went away;
  // the mount effect below re-attaches to it. Starting isLoading true keeps
  // the composer locked and the thinking indicator up from the first paint —
  // and keeps the persist effect's very first write from wiping the marker
  // before recovery has begun.
  const recoverOnMount =
    restored?.pendingTurn != null && restored.threadId != null && loadThreadMessages != null
  const [messages, setMessages] = useState<ChatMessage[]>(() => restored?.messages ?? [])
  const [isLoading, setIsLoading] = useState(recoverOnMount)
  const [pendingQuestion, setPendingQuestion] = useState<ClarifyQuestion | null>(
    () => restored?.pendingQuestion ?? null,
  )
  const [threadId, setThreadId] = useState<string | null>(() => restored?.threadId ?? null)
  const threadIdRef = useRef<string | null>(restored?.threadId ?? null)
  const abortRef = useRef<AbortController | null>(null)
  const turnSeq = useRef(maxAssistantId(restored?.messages))
  // Generation token. Bumped whenever a turn starts and whenever we tear a turn
  // down (load a past thread / new chat). Streaming callbacks capture the
  // generation they were created under and bail if it's since changed, so an
  // aborted stream still draining can't write onto a conversation the user has
  // since switched away from.
  const genRef = useRef(0)
  // Handle for the in-flight token-pacing animation frame, kept at hook scope so
  // a new chat / unmount can cancel a drain that's mid-flight.
  const rafRef = useRef<number | null>(null)
  // The request body of the most recent send, so `retry()` can re-send it
  // verbatim (resume_value / incident_id included). Cleared on any teardown;
  // a failed bubble restored from localStorage falls back to `retryBodyFor`.
  const lastBodyRef = useRef<StreamBody | null>(null)
  // Latest `owner`, read by the persist effect so it always stamps the current
  // identity without `owner` being one of its deps (which would let it re-save
  // the prior identity's transcript before the owner-change effect resets it).
  const ownerRef = useRef(owner)
  ownerRef.current = owner
  // Previous `owner`, to detect an in-place identity switch (below).
  const prevOwnerRef = useRef(owner)
  // Latest host callbacks, read at call time so runStream's identity doesn't
  // churn when a host passes inline lambdas.
  const onStallRef = useRef(onStall)
  onStallRef.current = onStall
  const onFailedRef = useRef(onFailed)
  onFailedRef.current = onFailed
  const cancelTurnRef = useRef(cancelTurn)
  cancelTurnRef.current = cancelTurn
  const loadThreadMessagesRef = useRef(loadThreadMessages)
  loadThreadMessagesRef.current = loadThreadMessages
  // Kind of the turn currently in flight (message vs clarify resume) — what the
  // persist effect stamps into `pendingTurn` so a remount knows how to judge
  // the replay. Null when no turn is in flight. Seeded from the restored blob
  // so the marker survives localStorage writes made DURING recovery.
  const pendingKindRef = useRef<PendingTurnKind | null>(
    recoverOnMount ? (restored?.pendingTurn?.kind ?? 'message') : null,
  )

  // Ask the backend to stop a thread's run — fire-and-forget (a failed cancel
  // just means the re-send waits out the busy ladder instead).
  const requestCancel = (tid: string | null) => {
    if (!tid) return
    try {
      void Promise.resolve(cancelTurnRef.current?.(tid)).catch(() => {})
    } catch {
      // a synchronous throw from the host callback — nothing to do
    }
  }

  // On unmount: stop the pacer and DETACH from any in-flight turn. The abort
  // closes only our reader — the backend keeps running the turn to completion
  // and checkpoints the answer (service._drive_turn); the pendingTurn marker
  // persisted below is what lets the next mount re-attach. Deliberately NOT a
  // cancel: navigating away must not kill the answer (the beta ticket).
  useEffect(
    () => () => {
      abortRef.current?.abort()
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    },
    [],
  )

  // Persist the transcript + thread id so a refresh restores the conversation.
  // A settled turn (isLoading false) writes the full transcript. While a turn
  // is IN FLIGHT we persist too — the just-sent user bubble, the thread id the
  // `thread` frame named, and the `pendingTurn` marker — but only when the
  // transcript's SHAPE changes, never per streamed token (that would thrash
  // localStorage), and without the half-streamed assistant bubble (the replay
  // is the source of truth for it). The marker is what lets a remount recover
  // the turn instead of silently dropping it. An empty, thread-less state
  // clears the store so "New chat" doesn't leave a stale blob.
  const inflightSigRef = useRef<string | null>(null)
  useEffect(() => {
    if (!persistKey) return
    if (messages.length === 0 && threadId === null) {
      if (!isLoading) clearPersisted(persistKey)
      return
    }
    if (isLoading) {
      const kind = pendingKindRef.current
      if (!kind) return
      const last = messages[messages.length - 1]
      const settled = last && last.role === 'assistant' && !last.failed ? messages.slice(0, -1) : messages
      const sig = `${threadId ?? ''}:${settled.length}:${pendingQuestion?.id ?? ''}`
      if (inflightSigRef.current === sig) return
      inflightSigRef.current = sig
      savePersisted(persistKey, {
        threadId,
        messages: settled,
        pendingQuestion,
        owner: ownerRef.current ?? null,
        pendingTurn: { kind },
      })
      return
    }
    inflightSigRef.current = null
    savePersisted(persistKey, { threadId, messages, pendingQuestion, owner: ownerRef.current ?? null })
  }, [persistKey, messages, threadId, pendingQuestion, isLoading])

  // The signed-in identity changed while this hook stayed mounted — an in-place
  // login that didn't remount us (single-origin app, switching companies in the
  // same tab). The in-memory threadId/transcript still belong to the PREVIOUS
  // tenant; without this they'd be sent under the new token and the backend would
  // 403 ("thread does not belong to client …"). Tear the old turn down and adopt
  // the new owner's persisted chat (normally none — login wiped it), so the next
  // message opens a fresh thread for the new tenant.
  useEffect(() => {
    if (prevOwnerRef.current === owner) return
    prevOwnerRef.current = owner
    genRef.current += 1
    abortRef.current?.abort()
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    lastBodyRef.current = null
    pendingKindRef.current = null
    const next = persistKey ? loadPersisted(persistKey, owner) : null
    threadIdRef.current = next?.threadId ?? null
    turnSeq.current = maxAssistantId(next?.messages)
    setThreadId(next?.threadId ?? null)
    setMessages(next?.messages ?? [])
    setPendingQuestion(next?.pendingQuestion ?? null)
    setIsLoading(false)
  }, [owner, persistKey])

  const runStream = useCallback(
    async (
      initialBody: StreamBody,
      // A manual retry reuses the failed bubble instead of appending a new one.
      reuse?: { turnId: number; attempt: number },
    ) => {
      setIsLoading(true)
      setPendingQuestion(null)
      // Stamp what kind of turn is in flight BEFORE React re-renders, so the
      // persist effect's first in-flight write already carries the marker.
      pendingKindRef.current = initialBody.resume_value != null ? 'resume' : 'message'

      // Per-turn state. The assistant bubble is tagged with `turnId` and always
      // patched BY ID, so an interleaved question/download can never misdirect a
      // token append to the wrong message. `gen` tags the whole turn so callbacks
      // from a torn-down stream stop touching state (see genRef).
      const gen = ++genRef.current
      const turnId = reuse?.turnId ?? ++turnSeq.current
      // Which send this is: 1 for the original, +1 per stall re-send (automatic
      // or via retry()). Busy waits re-send too but belong to the same attempt.
      let attempt = reuse?.attempt ?? 1
      const thinking: ThinkingEntry[] = []
      let reasoning = ''
      let started = reuse != null

      const ensureBubble = () => {
        if (started) return
        started = true
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', id: turnId, content: '', thinking: [...thinking], reasoning },
        ])
      }
      // `patch` may be a function when the new value depends on the current
      // bubble (appending a download to the ones already offered this turn).
      const patchAssistant = (
        patch:
          | Partial<AssistantMessage>
          | ((current: AssistantMessage) => Partial<AssistantMessage>),
      ) =>
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.role === 'assistant' && m.id === turnId)
          if (i < 0) return prev
          const current = prev[i] as AssistantMessage
          const next = [...prev]
          next[i] = {
            ...current,
            ...(typeof patch === 'function' ? patch(current) : patch),
          } as ChatMessage
          return next
        })
      // Local mirror of the bubble's content (kept in sync by every mutation
      // below) plus the committed prefix: once the backend signals
      // `addendum_start`, the audit has accepted the streamed answer and the
      // agent is APPENDING an update — resets must preserve the committed part
      // instead of blanking the bubble.
      let live = ''
      let committed = ''
      const appendContent = (chunk: string) => {
        live += chunk
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.role === 'assistant' && m.id === turnId)
          if (i < 0) return prev
          const cur = prev[i]
          if (cur.role !== 'assistant') return prev
          const next = [...prev]
          next[i] = { ...cur, content: cur.content + chunk }
          return next
        })
      }

      // --- token pacing: buffer tokens, drain on a rAF cadence (see PACE_*) -----
      let pending = ''
      const pump = () => {
        rafRef.current = null
        if (!pending) return
        const n = Math.max(PACE_MIN, Math.ceil(pending.length / PACE_DIV))
        const slice = pending.slice(0, n)
        pending = pending.slice(n)
        appendContent(slice)
        if (pending) schedule()
      }
      const schedule = () => {
        if (rafRef.current == null) rafRef.current = requestAnimationFrame(pump)
      }
      const enqueueToken = (chunk: string) => {
        ensureBubble()
        if (paced) {
          pending += chunk
          schedule()
        } else {
          appendContent(chunk)
        }
      }
      const stopPump = () => {
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }
      // Flush whatever is buffered immediately (stream end / no `final` event).
      const flushNow = () => {
        stopPump()
        if (pending) {
          appendContent(pending)
          pending = ''
        }
      }
      // A new answer pass is starting (the agent called a tool, or a quality
      // rerun discarded the last draft). Drop the buffered + rendered draft so
      // the bubble shows only the current answer — never a pile of stale
      // drafts. An answer committed by `addendum_start` survives the reset.
      const resetAnswer = () => {
        // The draft is moved, not lost: what the model narrated on its way to
        // a tool call is a thought, so it belongs in the Thinking block rather
        // than deleted outright — a tester who watched it appear in the bubble
        // and then vanish should still be able to find it.
        const dropped = (live.slice(committed.length) + pending).trim()
        if (dropped) reasoning += (reasoning ? '\n\n' : '') + dropped
        pending = ''
        live = committed
        if (started) patchAssistant({ content: committed, reasoning })
      }
      // A fresh send is about to start on this bubble (a stall re-send, or a
      // manual retry): everything the previous attempt streamed is stale —
      // trace, reasoning, draft, downloads, the failure — so the bubble starts
      // clean and shows which attempt it is.
      const resetForAttempt = () => {
        stopPump()
        pending = ''
        thinking.length = 0
        reasoning = ''
        live = ''
        committed = ''
        ensureBubble()
        patchAssistant({
          content: '',
          thinking: [],
          reasoning: '',
          downloads: undefined,
          download: undefined,
          failed: undefined,
          attempt,
        })
      }

      const pushThinking = (entry: ThinkingEntry) => {
        thinking.push(entry)
        if (started) patchAssistant({ thinking: [...thinking] })
      }

      const onEvent = ({ event, data }: SSEFrame) => {
        // A newer turn started, or the user loaded a different thread / hit "new
        // chat" — this stream is stale, so drop its events instead of writing
        // them onto whatever conversation is on screen now.
        if (gen !== genRef.current) return
        const d = (data ?? {}) as Record<string, unknown>
        switch (event) {
          case 'thread':
            if (d.thread_id) {
              threadIdRef.current = str(d.thread_id)
              setThreadId(threadIdRef.current)
            }
            break
          case 'reasoning_token':
            if (d.content) {
              reasoning += str(d.content)
              ensureBubble()
              patchAssistant({ reasoning })
            }
            break
          case 'token':
            if (d.content) enqueueToken(str(d.content))
            break
          case 'narration_reset':
            // The model narrated its way to a tool call ("Now I can see the
            // pattern. Let me also look at…"). The backend spots the tool call
            // on its first delta, which is well before the `tool` event below —
            // that one is emitted by hand from inside each tool's body, and a
            // third of the tools never emit it at all.
            resetAnswer()
            break
          case 'answer_reset':
            // Backend signal that the prior streamed draft is being discarded
            // (a quality rerun is writing a fresh answer). Clear the bubble.
            // Kept for back-compat: current flows send addendum_start instead.
            resetAnswer()
            break
          case 'addendum_start':
            // The completeness audit kept the streamed answer and the agent is
            // appending an update below it: freeze what's rendered (plus any
            // buffered tail) as committed, add a separator, and let the
            // addendum stream in after it. Tool events during the addendum
            // pass reset only the uncommitted part.
            flushNow()
            if (live) {
              ensureBubble()
              committed = `${live}\n\n---\n\n`
              live = committed
              patchAssistant({ content: committed })
            }
            break
          case 'final':
            // Authoritative final answer. Stop the pacer and set it outright.
            if (d.message) {
              stopPump()
              pending = ''
              live = str(d.message)
              ensureBubble()
              patchAssistant({ content: live, thinking: [...thinking] })
            }
            break
          case 'question': {
            const q = data as ClarifyQuestion
            setMessages((prev) => [...prev, { role: 'question', question: q }])
            setPendingQuestion(q)
            break
          }
          case 'download': {
            // Store the raw payload so the shared DownloadCard reads whatever
            // fields the backend sends (filename/url/row_count/expires/…).
            // APPEND: one turn can offer several files (an SLA answer sends the
            // PDF and the CSV), and a single slot silently kept only the last.
            ensureBubble()
            patchAssistant((prev) => {
              const next = [...(prev.downloads ?? []), d as DownloadInfo]
              return { downloads: next, download: next[0] }
            })
            break
          }
          case 'findings_card': {
            // The producer's card, the moment it was stashed — render the
            // ref-only fence from it instead of a skeleton. The hydrated
            // `final` text supersedes it (a hydrated fence never consults
            // this map), so there is nothing to clear.
            const ref = (d as { ref?: unknown }).ref
            const block = (d as { block?: unknown }).block
            if (typeof ref === 'string' && ref && block && typeof block === 'object') {
              ensureBubble()
              patchAssistant((prev) => ({ cards: { ...(prev.cards ?? {}), [ref]: block as object } }))
            }
            break
          }
          case 'reasoning_decision':
            pushThinking({
              kind: 'reasoning_decision',
              needs_reasoning: Boolean(d.needs_reasoning),
              complexity: str(d.complexity),
              why: str(d.why),
            })
            break
          case 'route':
            pushThinking({ kind: 'route', flow: str(d.flow), reasoning: str(d.reasoning) })
            break
          case 'memory_load':
            pushThinking({ kind: 'memory_load', client: strArr(d.client), skills: strArr(d.skills) })
            break
          case 'memory_write':
            pushThinking({ kind: 'memory_write', slug: str(d.slug), type: d.type ? str(d.type) : undefined })
            break
          case 'skill_save':
            pushThinking({ kind: 'skill_save', slug: str(d.slug) })
            break
          case 'tool':
            // The agent is calling a tool, so anything it streamed just now was
            // pre-tool narration ("let me check…"), not the answer. Move on:
            // record the tool in the trace and clear the draft from the bubble.
            pushThinking({ kind: 'tool', name: str(d.name), args: d.args })
            resetAnswer()
            break
          case 'tool_result':
            pushThinking({
              kind: 'tool_result',
              name: str(d.name),
              summary: d.summary ? str(d.summary) : undefined,
              error: d.error ? str(d.error) : undefined,
            })
            break
          case 'node':
            if (d.name && d.phase === 'complete') pushThinking({ kind: 'node', name: str(d.name) })
            break
          case 'error': {
            const detail = str(d.detail) || 'stream error'
            // The per-thread single-flight guard: our previous attempt is still
            // winding down server-side. Distinguished by code so the retry loop
            // can wait it out instead of failing the turn on the prose.
            if (d.code === BUSY_CODE) throw new TurnBusyError(detail)
            throw new Error(detail)
          }
          // 'done' and any unknown events: ignore
        }
      }

      // The user hit Stop (or a stall re-send was interrupted mid-wait): keep
      // whatever already streamed (including the pacer's buffered tail), then
      // mark the turn as interrupted so the transcript shows the reply was cut
      // short, not finished.
      const markInterrupted = () => {
        flushNow()
        ensureBubble()
        patchAssistant({ content: live ? `${live}\n\n*Interrupted*` : '*Interrupted*' })
      }

      const policy: RetryPolicy = {
        stallRetries,
        stallRetryDelayMs: STALL_RETRY_DELAY_MS,
        busyRetryDelaysMs: BUSY_RETRY_DELAYS_MS,
      }
      let state: RetryState = INITIAL_RETRY_STATE
      let body = initialBody
      if (reuse) resetForAttempt()

      try {
        for (;;) {
          // A re-send must land on the thread the first attempt opened — the
          // `thread` frame arrives before any work — or it would start a second
          // conversation for the same question.
          body = { ...body, thread_id: threadIdRef.current ?? body.thread_id }
          lastBodyRef.current = body

          const controller = new AbortController()
          abortRef.current = controller
          // Progress watchdog: every frame the reducer sees restarts it; heartbeat
          // comments never become frames, so a wedged model trips it while a
          // slow-but-alive one does not. Tripping aborts the fetch, which
          // surfaces below as an AbortError we can tell apart from Stop.
          const watchdog = createStallWatchdog(stallTimeoutMs, () => controller.abort())
          let outcome: AttemptOutcome
          try {
            await stream(
              body,
              (frame) => {
                watchdog.touch()
                onEvent(frame)
              },
              controller.signal,
            )
            watchdog.stop()
            // Torn down (new chat / thread switch / owner change bump the gen
            // BEFORE aborting): don't touch the pump or the transcript — the raf
            // handle and the messages may already belong to a newer turn.
            if (gen !== genRef.current) return
            // Stream ended cleanly: render any tokens still buffered by the pacer
            // (covers flows that stream the answer without a trailing `final`).
            flushNow()
            return
          } catch (err) {
            watchdog.stop()
            if (gen !== genRef.current) return
            if (isAbort(err)) {
              if (!watchdog.tripped) {
                markInterrupted()
                return
              }
              // The watchdog killed a wedged attempt. Aborting the fetch only
              // stops WATCHING (turns survive disconnects now) — ask the
              // server to cancel the run for real, so the re-send below finds
              // the thread free instead of camping on the busy ladder.
              requestCancel(threadIdRef.current)
              outcome = { kind: 'stall' }
            } else if (err instanceof TurnBusyError) {
              outcome = { kind: 'busy', detail: err.message }
            } else {
              outcome = {
                kind: 'error',
                detail: err instanceof Error ? err.message : 'Something went wrong.',
              }
            }
          } finally {
            if (abortRef.current === controller) abortRef.current = null
          }

          const plan = planRetry(outcome, state, policy, { timeoutMs: stallTimeoutMs, attempts: attempt })
          if (outcome.kind === 'stall') {
            onStallRef.current?.({
              attempt,
              willRetry: plan.retry,
              timeoutMs: stallTimeoutMs,
              threadId: threadIdRef.current,
            })
          }
          if (!plan.retry) {
            // Give up. Whatever streamed before the failure stays in the bubble
            // (a half answer beats a blank), the failure rides alongside it, and
            // the host renders the retry affordance from `failed`.
            flushNow()
            ensureBubble()
            patchAssistant({ failed: plan.failure })
            onFailedRef.current?.({ ...plan.failure, attempts: attempt, threadId: threadIdRef.current })
            return
          }

          state = plan.state
          if (outcome.kind === 'stall') attempt += 1
          resetForAttempt()
          // Wait for the server to notice the abort and release the thread.
          // Stop during the wait counts as an interrupt, exactly like Stop
          // mid-stream; a teardown (gen bump) just drops the turn.
          const waiter = new AbortController()
          abortRef.current = waiter
          try {
            await abortableDelay(plan.delayMs, waiter.signal)
          } finally {
            if (abortRef.current === waiter) abortRef.current = null
          }
          if (gen !== genRef.current) return
          if (waiter.signal.aborted) {
            markInterrupted()
            return
          }
        }
      } finally {
        // Guarded like the catch: a stale turn's cleanup must not clobber a
        // newer turn's spinner or its live AbortController. Clearing the kind
        // lets the persist effect's next settled write drop the pendingTurn
        // marker. (After an UNMOUNT abort this cleanup still runs, but no
        // effect ever re-runs on the dead component — the blob keeps the
        // marker, which is exactly what the next mount's recovery needs.)
        if (gen === genRef.current) {
          pendingKindRef.current = null
          setIsLoading(false)
        }
      }
    },
    [stream, paced, stallTimeoutMs, stallRetries],
  )

  const send = useCallback(
    (text: string, opts?: { incidentId?: string }) => {
      const t = text.trim()
      if (!t || isLoading || pendingQuestion) return
      setMessages((prev) => [...prev, { role: 'user', content: t }])
      void runStream({
        message: t,
        thread_id: threadIdRef.current ?? undefined,
        incident_id: opts?.incidentId,
      })
    },
    [isLoading, pendingQuestion, runStream],
  )

  const sendResume = useCallback(
    (value: string, label?: string) => {
      // Mirror send's guard: don't resume if a turn is in flight or no question
      // is actually pending (defends against a double-click race).
      if (isLoading || !pendingQuestion || !threadIdRef.current) return
      setMessages((prev) => [...prev, { role: 'user', content: label ?? value }])
      void runStream({ thread_id: threadIdRef.current, resume_value: value })
    },
    [isLoading, pendingQuestion, runStream],
  )

  const stop = useCallback(() => {
    if (!abortRef.current) return
    // Stopping means STOP: the abort only detaches our reader (turns outlive
    // disconnects server-side), so also ask the server to kill the run.
    requestCancel(threadIdRef.current)
    abortRef.current.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const retry = useCallback(() => {
    if (isLoading) return
    // Only the LAST turn is retryable: re-running an older failed turn would
    // append its answer out of order, below newer messages.
    const last = messages[messages.length - 1]
    if (!last || last.role !== 'assistant' || !last.failed) return
    const body = lastBodyRef.current ?? retryBodyFor(messages, last.id, threadIdRef.current)
    if (!body) return
    void runStream(body, { turnId: last.id, attempt: (last.attempt ?? 1) + 1 })
  }, [isLoading, messages, runStream])

  const startNewChat = useCallback(() => {
    genRef.current += 1
    abortRef.current?.abort()
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    lastBodyRef.current = null
    pendingKindRef.current = null
    threadIdRef.current = null
    setThreadId(null)
    setMessages([])
    setPendingQuestion(null)
    setIsLoading(false)
  }, [])

  // Rebuild a replayed transcript into live ChatMessages. Assistant bubbles
  // need a fresh turn id (kept ahead of turnSeq so the next live turn can't
  // collide) and the streaming-only fields (thinking/reasoning) history omits.
  const rebuildTranscript = useCallback(
    (loadedMsgs: LoadedMessage[]): ChatMessage[] =>
      loadedMsgs.map((m) =>
        m.role === 'assistant'
          ? { role: 'assistant', id: ++turnSeq.current, content: m.content, thinking: [], reasoning: '' }
          : { role: 'user', content: m.content },
      ),
    [],
  )

  // Replace the live transcript with a replayed thread — plus the clarify
  // question it is paused on, so an interrupted clarify can be answered again
  // instead of replaying as a dead unanswered thread.
  const adoptLoaded = useCallback(
    (loadedId: string, loadedMsgs: LoadedMessage[], question: ClarifyQuestion | null) => {
      const loaded = rebuildTranscript(loadedMsgs)
      if (question) loaded.push({ role: 'question', question })
      threadIdRef.current = loadedId
      pendingKindRef.current = null
      setThreadId(loadedId)
      setMessages(loaded)
      setPendingQuestion(question)
      setIsLoading(false)
    },
    [rebuildTranscript],
  )

  // A recovered turn turned out dead: render the replay (when we have one),
  // re-appending the local question if it never reached the checkpoint, and a
  // failed bubble whose Retry re-sends it.
  const failRecovered = useCallback(
    (tid: string, detail: LoadedThread | null, pending: PendingTurnInfo, mergeUserText: boolean) => {
      const bubble: ChatMessage = {
        role: 'assistant',
        id: ++turnSeq.current,
        content: '',
        thinking: [],
        reasoning: '',
        failed: { reason: 'error', detail: RECOVERED_FAILURE_DETAIL },
      }
      pendingKindRef.current = null
      if (detail) {
        const base = rebuildTranscript(detail.messages)
        if (mergeUserText && pending.userText) base.push({ role: 'user', content: pending.userText })
        base.push(bubble)
        threadIdRef.current = tid
        setThreadId(tid)
        setMessages(base)
        setPendingQuestion(null)
      } else {
        // The API was unreachable — keep whatever transcript is on screen
        // (the restored user bubble included) and add the card to it.
        setMessages((prev) => [...prev, bubble])
      }
      setIsLoading(false)
    },
    [rebuildTranscript],
  )

  // Re-attach to a turn that outlived its stream (see turnRecovery.ts): poll
  // the replay while the server reports the turn in flight, adopt the answer
  // (or the clarify question) once it lands, or fail the turn with a Retry.
  const recoverTurn = useCallback(
    async (tid: string, pending: PendingTurnInfo) => {
      const fetchThread = loadThreadMessagesRef.current
      if (!fetchThread) return
      const gen = ++genRef.current
      pendingKindRef.current = pending.kind
      setIsLoading(true)
      const controller = new AbortController()
      abortRef.current = controller
      const deadline = Date.now() + RECOVER_DEADLINE_MS
      let fetchFailures = 0
      try {
        for (;;) {
          let detail: LoadedThread | null = null
          try {
            detail = await fetchThread(tid)
            fetchFailures = 0
          } catch {
            fetchFailures += 1
          }
          if (gen !== genRef.current) return
          // Stop pressed while we fetched/waited: stop() already asked the
          // server to cancel — leave the transcript as it stands.
          if (controller.signal.aborted) {
            pendingKindRef.current = null
            setIsLoading(false)
            return
          }
          let action: RecoveryAction =
            detail != null
              ? planRecovery(detail, pending)
              : fetchFailures >= RECOVER_MAX_FETCH_FAILURES
                ? { kind: 'failed', mergeUserText: false }
                : { kind: 'poll' }
          if (action.kind === 'poll' && Date.now() > deadline) {
            // Reported busy past the server's own turn budget: wedged.
            action = { kind: 'failed', mergeUserText: false }
          }
          if (action.kind === 'adopt' && detail != null) {
            adoptLoaded(detail.id || tid, detail.messages, detail.question ?? null)
            return
          }
          if (action.kind === 'failed') {
            failRecovered(tid, detail, pending, action.mergeUserText)
            return
          }
          await abortableDelay(RECOVER_POLL_MS, controller.signal)
          if (gen !== genRef.current) return
          if (controller.signal.aborted) {
            pendingKindRef.current = null
            setIsLoading(false)
            return
          }
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null
      }
    },
    [adoptLoaded, failRecovered],
  )

  // A pendingTurn marker survived from a previous mount: a turn was in flight
  // when the page went away (SPA navigation, refresh, tab close). Re-attach to
  // it — or, when the turn died before the server even named a thread, fail it
  // visibly so the question isn't silently lost. Mount-only; StrictMode's
  // double-run is absorbed by the gen guard and the already-failed check.
  useEffect(() => {
    const p = restored?.pendingTurn
    if (!p) return
    if (restored?.threadId && loadThreadMessagesRef.current) {
      void recoverTurn(restored.threadId, {
        kind: p.kind === 'resume' ? 'resume' : 'message',
        userText: lastUserText(restored.messages),
      })
      return
    }
    pendingKindRef.current = null
    setIsLoading(false)
    setMessages((prev) => {
      const last = prev[prev.length - 1]
      if (last && last.role === 'assistant' && last.failed) return prev
      return [
        ...prev,
        {
          role: 'assistant',
          id: ++turnSeq.current,
          content: '',
          thinking: [],
          reasoning: '',
          failed: { reason: 'error', detail: RECOVERED_NO_THREAD_DETAIL },
        },
      ]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadThread = useCallback(
    async (id: string, messages?: LoadedMessage[]) => {
      // Resolve the messages BEFORE touching any state: a failed fetch
      // (404/expired/offline) must leave the current conversation exactly as it
      // was, so the caller can surface an error without losing what's on screen.
      let loadedId = id
      let loadedMsgs: LoadedMessage[]
      let detail: LoadedThread | null = null
      if (messages) {
        loadedMsgs = messages
      } else if (loadThreadMessages) {
        detail = await loadThreadMessages(id)
        loadedId = detail.id
        loadedMsgs = detail.messages
      } else {
        throw new Error('loadThread: pass messages, or configure loadThreadMessages')
      }

      // Committed to the switch — invalidate the old turn's callbacks, then tear
      // down anything in flight from it.
      genRef.current += 1
      abortRef.current?.abort()
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      lastBodyRef.current = null

      if (detail?.busy) {
        // A turn is running on this thread RIGHT NOW — it outlived its stream
        // (ours from before a navigation, or another tab's). Show what the
        // checkpoint has and re-attach instead of rendering a dead unanswered
        // question.
        adoptLoaded(loadedId, loadedMsgs, null)
        void recoverTurn(loadedId, { kind: 'message', userText: lastUserText(loadedMsgs) })
        return
      }
      adoptLoaded(loadedId, loadedMsgs, detail?.question ?? null)
    },
    [loadThreadMessages, adoptLoaded, recoverTurn],
  )

  return {
    messages,
    isLoading,
    pendingQuestion,
    threadId,
    send,
    sendResume,
    stop,
    retry,
    startNewChat,
    loadThread,
  }
}
